import { useTranslation } from "react-i18next";

import { peekLine, type RoomPeek } from "@/engine/state/roomPeek";
import { roomName } from "@/engine/state/roomIdentity";
import { cn } from "@/engine/support/classNames";

import { type AppAvatar } from "@/components/AppShell";

export interface EntryRoomPreviewProps {
  /** The code the visitor arrived holding. */
  code: string;
  /** What the room behind it answered. Only an answer is drawn. */
  peek: RoomPeek;
  /** The visitor's own animal and colour, for the seat that is waiting. */
  avatar: AppAvatar;
  /** Opens the create door instead, for somebody who would rather light their own. */
  onCreateInstead: () => void;
  /** The way in, and the identity row beside it, drawn by the caller. */
  children: React.ReactNode;
}

/**
 * The room somebody was invited to, drawn instead of a form.
 *
 * Nine entries out of ten arrive on a link, and for those the code is known and
 * the room has already answered. Hiding that behind a card throws away an answer
 * already in hand: the screen can say where they have landed rather than asking
 * them to press and find out. Entering then stops being a transition — the scene
 * is already there, and the press takes the seat that was waiting.
 *
 * **What is drawn is only what is known.** A peek answers with a count, not with
 * a roster: who is at the fire, what they are called and which of them is
 * talking are presence facts belonging to a room this visitor has not joined.
 * So the seats that are taken are drawn taken and unnamed. Inventing four names
 * to fill the screen would make it say something it cannot know, which
 * is worse than a quieter screen — and the roster is #506's to settle.
 */
export function EntryRoomPreview({
  code,
  peek,
  avatar,
  onCreateInstead,
  children,
}: EntryRoomPreviewProps) {
  const { t } = useTranslation();
  const line = peekLine(peek);
  const taken = peek.kind === "answered" ? peek.count : 0;

  return (
    /* The middle of the frame, not the whole of it: past about a thousand
       pixels the seats and the way in stop being a room and become a row of
       things strung across a wall. */
    <div
      data-testid="entry-room-preview"
      className="mx-auto flex min-h-0 w-full max-w-[62rem] flex-1 flex-col"
    >
      <header className="mb-3 flex items-center gap-3">
        <h2
          data-testid="entry-preview-name"
          className="truncate text-lg font-semibold text-foreground"
        >
          {roomName(code)}
        </h2>
        <code className="rounded-md bg-muted/40 px-2 py-0.5 font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {code}
        </code>
        <button
          type="button"
          data-testid="entry-preview-create"
          onClick={onCreateInstead}
          className="ml-auto shrink-0 rounded-lg border border-border/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
        >
          {t("entry.create")}
        </button>
      </header>

      {line && (
        <p
          data-testid="entry-preview-line"
          className="mb-3 text-center text-sm text-muted-foreground"
        >
          {t(line.key, { count: line.count })}
        </p>
      )}

      {/* The seats. Taken ones are drawn taken and unnamed — a peek answers
        with a count, and naming anybody from it would be the screen saying
        what it cannot know. */}
      <ul
        data-testid="entry-seats"
        className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3"
      >
        {Array.from({ length: taken }, (_, i) => (
          <li
            key={`taken-${i}`}
            data-testid="entry-seat-taken"
            className="flex h-20 items-center justify-center rounded-xl border border-border/50 bg-muted/20"
          >
            {/* Filled rather than outlined. An empty ring where a person is
              reads as something still loading, and the book rules out a shell
              with placeholders where people should be; a solid one says
              somebody is sitting there, which is the whole of what a count
              can honestly say. */}
            <span
              aria-hidden
              className="h-9 w-9 rounded-full bg-primary/25 ring-1 ring-primary/40"
            />
          </li>
        ))}
        <li
          data-testid="entry-seat-yours"
          className={cn(
            "flex h-20 flex-col items-center justify-center gap-1 rounded-xl",
            "border-2 border-dashed border-primary/60 bg-primary/5",
          )}
        >
          <span
            aria-hidden
            className="flex h-9 w-9 items-center justify-center rounded-full text-lg"
            style={{
              backgroundColor: `${avatar.color}33`,
              border: `2px solid ${avatar.color}`,
            }}
          >
            {avatar.emoji}
          </span>
          <span className="text-xs text-muted-foreground">
            {t("entry.yourSeat")}
          </span>
        </li>
      </ul>

      {/* The way in, handed down rather than rebuilt: it is the same press, the
        same name and the same level bar as the card's. */}
      <div className="mt-4 rounded-2xl glass border border-border/50 p-4">
        {children}
      </div>
    </div>
  );
}

/**
 * Who is inside, against who is not — the list beside the room, in the frame's
 * third column.
 *
 * It is drawn by whoever draws the frame rather than by the room itself: one
 * shell holds the entry screen in every state, and a component that built its
 * own would be a second frame inside the first.
 */
export function EntryRoomRoster({
  taken,
  avatar,
  name,
}: {
  /** How many the peek counted. It answers with a number and no names. */
  taken: number;
  avatar: AppAvatar;
  name: string;
}) {
  const { t } = useTranslation();

  return (
    <aside
      data-testid="entry-roster"
      className="hidden min-[1080px]:flex w-48 shrink-0 flex-col gap-3 border-l border-border/50 bg-card/30 p-4"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("entry.atTheFireHeading")} — {taken}
      </p>
      <ul className="flex flex-col gap-2">
        {Array.from({ length: taken }, (_, i) => (
          <li key={`at-${i}`} className="flex items-center gap-2">
            <span
              aria-hidden
              className="h-6 w-6 rounded-full bg-primary/25 ring-1 ring-primary/40"
            />
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("entry.outside")}
      </p>
      <span className="flex items-center gap-2 text-sm text-foreground">
        <span
          aria-hidden
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm"
          style={{
            backgroundColor: `${avatar.color}33`,
            border: `2px solid ${avatar.color}`,
          }}
        >
          {avatar.emoji}
        </span>
        <span className="truncate">{name}</span>
      </span>
    </aside>
  );
}
